import { AccountType } from "shared/types/accounts";

import { getAccountPlatformType } from "./account-platform";

export const TWITTER_SHORT_LIMIT = 280;
export const TWITTER_LONGFORM_LIMIT = 25_000;

export type TwitterLongformCapability =
  | "capable" // confirmed X Premium — can post up to 25,000 chars
  | "not-capable" // confirmed NOT premium — limited to 280 chars
  | "unknown"; // no twData yet (legacy/never refreshed) — treat as warn-only

/**
 * Whether a connected X account can publish long-form (>280 char) tweets.
 *
 * Uses `subscription_type` only:
 * - "Premium" => capable
 * - "None" => not-capable
 * - anything else / missing => unknown
 *
 * Returns "unknown" when we have no capability data at all — the account was
 * connected before this feature or its refresh hasn't run yet. Callers warn
 * (never hard-block) in that case.
 *
 * For non-TW accounts the helper returns "unknown" rather than "not-capable":
 * the account was never assessed, so we don't claim a verdict. Callers that
 * branch on "not-capable" to drive UI (e.g. the "X Premium" off chip) should
 * still filter to TW accounts first.
 */
export function getTwitterLongformCapability(
  account: AccountType
): TwitterLongformCapability {
  if (getAccountPlatformType(account) !== "TW") {
    return "unknown";
  }
  const twData = account.twData;
  if (!twData) {
    return "unknown";
  }
  const sub = twData.subscription_type;
  if (sub === "Premium") {
    return "capable";
  }
  if (sub === "None") {
    return "not-capable";
  }
  return "unknown";
}
